import { createFileRoute, Link } from "@tanstack/react-router";
import "@fontsource/fragment-mono/latin-400.css";
import "@fontsource/special-gothic-expanded-one/latin-400.css";
import { CASES, depthOf } from "@/components/soad/content";
import { Film, Gallery } from "@/components/soad/Gallery";
import { pad } from "@/components/soad/hooks";
import { Loupe, Page, PageHead, titleLines } from "@/components/soad/Pages";
import { WORKS } from "@/components/soad/works";
import "@/styles/soad.css";

export const Route = createFileRoute("/case/$id")({ component: CasePage });

function CasePage() {
  const { id } = Route.useParams();
  const n = CASES.findIndex(work => work.id === id);
  const work = CASES[n];

  if (!work) {
    return (
      <Page>
        <PageHead label="case study" lines={["Not found"]}>
          <p className="sd-page-lede">
            there is no case study at this address. <Link to="/">back to the studio</Link>.
          </p>
        </PageHead>
      </Page>
    );
  }

  const extra = WORKS[work.id];
  const film = extra?.film;
  const before = CASES[(n + CASES.length - 1) % CASES.length];
  const after = CASES[(n + 1) % CASES.length];

  return (
    <Page>
      {/* keyed so the title sharpens again when you step to the next case */}
      <PageHead
        key={work.id}
        label={`case study ${pad(n + 1)} / ${pad(CASES.length)} / ${work.code}`}
        lines={titleLines(work.title)}
      >
        <p className="sd-label">{work.meta}</p>
      </PageHead>
      {film && !film.upright && <Film key={film.src} film={film} />}
      {/* a case with its own film or galleries needs no still: the facts sit in the text column, beside a portrait film if there is one */}
      <div className="sd-work" data-plain={extra && !film?.upright ? "" : undefined}>
        {film?.upright && <Film key={film.src} film={film} />}
        {!extra && (
          <Loupe
            key={work.id}
            src={work.src}
            depth={depthOf(work.src)}
            alt={work.title}
            hint="move over the work / press to change the reading"
          />
        )}
        <div className="sd-work-text">
          <h2>{work.body}</h2>
          <dl>
            {work.facts.map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <Link className="sd-button" to="/contact">
            <span>have a project like this?</span>
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
      {extra?.shelves.map(shelf => (
        <Gallery key={shelf.id} shelf={shelf} />
      ))}
      <nav className="sd-work-step" aria-label="Other case studies">
        <Link to="/case/$id" params={{ id: before.id }}>
          <span className="sd-label">← previous</span>
          <span>{before.title}</span>
        </Link>
        <Link to="/case/$id" params={{ id: after.id }}>
          <span className="sd-label">next →</span>
          <span>{after.title}</span>
        </Link>
      </nav>
    </Page>
  );
}
